import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { DateTime } from "luxon";
import ms from "ms";
import {
  type InvoiceSummaryDto,
  InvoicesViewType,
  OrderInvoicesClient,
  SearchInvoicesRequest,
  SearchInvoicesRequestFilter,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { INVOICE_PORTAL_CUTOFF_DATE } from "../constants/filterConstants";
import { InvoicesQueryKeys } from "./InvoicesQueryKeys";

// Search also matches PO and order numbers, so an exact invoice may be on a later page.
const LOOKUP_PAGE_SIZE = 100;

export const normalizeInvoiceNumber = (
  invoiceNumber: string | null | undefined,
): string => invoiceNumber?.trim().toUpperCase() ?? "";

export const invoiceByNumberQueryKey = (
  invoiceNumber: string,
  view: InvoicesViewType,
) =>
  [
    InvoicesQueryKeys.Search,
    "by-number",
    normalizeInvoiceNumber(invoiceNumber),
    view,
  ] as const;

/**
 * Finds a single invoice by its exact number, for a drawer opened from the URL alone. The
 * backend requires a date range, so it spans the whole portal history. Resolves to null when
 * no invoice the user can see has that number. Also the source of an invoice's `orgUnitId`
 * for drawers stacked on a cold invoice drawer.
 */
export function useInvoiceByNumber(
  invoiceNumber: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<InvoiceSummaryDto | null, Error> {
  const api = useApi(OrderInvoicesClient);
  const { hasPermission, claimsLoading } = useClaims();
  const normalized = normalizeInvoiceNumber(invoiceNumber);
  // All requires the org-unit search permission; My is the only view left without it.
  const view = hasPermission(ShiOnePermission.orderSearchOrgUnit)
    ? InvoicesViewType.All
    : InvoicesViewType.My;

  return useQuery({
    queryKey: invoiceByNumberQueryKey(normalized, view),
    queryFn: async ({ signal }) => {
      const cancelSource = axios.CancelToken.source();
      const cancelRequest = () => cancelSource.cancel();
      signal?.addEventListener("abort", cancelRequest, { once: true });
      if (signal?.aborted) cancelRequest();

      try {
        const invoiceDateRange = [
          INVOICE_PORTAL_CUTOFF_DATE.toISODate()!,
          DateTime.now().plus({ days: 1 }).toISODate()!,
        ];
        for (let page = 1; ; page += 1) {
          cancelSource.token.throwIfRequested();
          const result = await api.searchInvoices(
            new SearchInvoicesRequest({
              view,
              page,
              pageSize: LOOKUP_PAGE_SIZE,
              searchString: invoiceNumber!.trim(),
              sortByColumn: "InvoiceNumber",
              isDescending: false,
              searchFilterInclude: new SearchInvoicesRequestFilter({
                invoiceDateRange,
              }),
            }),
            cancelSource.token,
          );
          cancelSource.token.throwIfRequested();
          const match = result.items?.find(
            (item) => normalizeInvoiceNumber(item.invoiceNumber) === normalized,
          );
          if (match) return match;
          if (!Number.isInteger(result.totalPages) || result.totalPages! < 0) {
            throw new Error("Invoice search did not return valid pagination metadata.");
          }
          if (page >= result.totalPages!) return null;
        }
      } finally {
        signal?.removeEventListener("abort", cancelRequest);
      }
    },
    enabled: enabled && normalized.length > 0 && !claimsLoading,
    staleTime: ms("1m"),
  });
}
